import { EventEmitter, Injectable } from "@angular/core";
import { DOMManagerService } from "./dommanager.service";
import { EventManagerAdminService } from "./event-manager/event-manager-admin.service";
import { KeyValAdminService } from "./key-val-admin.service";
import { BotidService } from "@shared-angular/services/ragai/botid.service";
import { SkillService } from "./skill.service";


type StageChange = {
  from?: StageDefinition;
  to?: StageDefinition;
};


type StageDefinition = {
  title: string;
  name: string;
  hidden?: boolean;
  skills: string[];

  forceSkills?: boolean;
  footer: boolean;
  enterFn?: () => Promise<void>;
  exitFn?: () => Promise<void>;
  sortFloat: number;
};
@Injectable({ providedIn: 'root' })
export class StageManagerService {
  private _stages: StageDefinition[] = [];
  private _currentIndex: number = -1;
  public currentStage: StageDefinition | undefined = undefined;
  private previousStage: StageDefinition | undefined = undefined;
  private KEYVAL_TYPE = "STAGE_MANAGER";
  private KEYVAL_KEY_CURRENT = "CURRENT_STAGE";
  private KEYVAL_KEY_PREVIOUS = "PREVIOUS_STAGE";
  public beforeStageChange = new EventEmitter<StageChange>();
  public afterStageChange = new EventEmitter<StageChange>();
  public stageEnter = new EventEmitter<StageDefinition>();
  public stageExit = new EventEmitter<StageDefinition>();
  private eventNamePrefix = "RAGAI.STAGES";


  constructor(private skillManager: SkillService,

    private keyValService: KeyValAdminService,
    private DOM: DOMManagerService,
    private botIdService: BotidService,
    private eventManager: EventManagerAdminService
  ) { }

  public add(name: string, stage: Partial<Omit<StageDefinition, "name">>): void {
    this.eventManager._trigger_RAGAI_EVENT(this, { name, stage });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const fnRegistry = (window as any).__RAGAI_STAGE_FN;
    const fullStage: StageDefinition = {
      title: stage.title ?? '',
      name,
      hidden: stage.hidden ?? false,
      skills: stage.skills ?? [],
      forceSkills: stage.forceSkills ?? false,
      footer: stage.footer ?? true,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      enterFn: fnRegistry?.[name + "_enter"] ?? undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      exitFn: fnRegistry?.[name + "_exit"] ?? undefined,
      sortFloat: stage.sortFloat ?? -1
    };
    if (this._stages.find(s => s.name === name)) {
      throw new Error(`Stage with name '${name}' already exists.`);
    }
    this._stages.push({ ...fullStage, name });
  }


  public remove(name: string): void {
    this.eventManager._trigger_RAGAI_EVENT(this, { name });
    const index = this._stages.findIndex(s => s.name === name);
    if (index === -1) throw new Error(`Stage with name '${name}' not found.`);
    this._stages.splice(index, 1);
    if (this._currentIndex === index) this._currentIndex = -1;
    else if (this._currentIndex > index) this._currentIndex--;
  }

  public hide(name: string): void {
    const index = this._stages.findIndex(s => s.name === name);
    if (index === -1) throw new Error(`Stage with name '${name}' not found.`);
    if (this._stages[index]) {
      this._stages[index].hidden = true;
      if (this._currentIndex === index) this._currentIndex = -1;
      else if (this._currentIndex > index) this._currentIndex--;
    }
  }

  public show(name: string): void {
    const index = this._stages.findIndex(s => s.name === name);
    if (index === -1) throw new Error(`Stage with name '${name}' not found.`);
    if (this._stages[index]) {
      this._stages[index].hidden = false;
      if (this._currentIndex === index) this._currentIndex = -1;
      else if (this._currentIndex > index) this._currentIndex--;
    }
  }

  public async goToRandom(possibleNames?: string[]): Promise<void> {
    let candidates: StageDefinition[];
    if (possibleNames && possibleNames.length > 0) {
      candidates = this._stages.filter(stage =>
        possibleNames.includes(stage.name)
      );
    } else {
      candidates = this._stages.filter(stage => !stage.hidden);
    }

    if (candidates.length === 0) {
      throw new Error("No matching stages available.");
    }

    const randomStage = candidates[Math.floor(Math.random() * candidates.length)];
    if (randomStage) {
      await this.goToName(randomStage.name);
    }
  }


  public async goToIndex(index: number) {
    this.eventManager._trigger_RAGAI_EVENT(this, { index });
    if (index < 0 || index >= this._stages.length) {
      throw new Error(`Stage with index '${index + ""}' not found.`);
    }
    if (this._stages[index]) {
      this._currentIndex = index;
      await this._activate();
    }
  }
  public async goTo(value: number | string): Promise<void> {
    this.eventManager._trigger_RAGAI_EVENT(this, { value });
    if (typeof value === 'number') {
      return this.goToIndex(value);
    } else if (typeof value === 'string') {
      return this.goToName(value);
    } else {
      throw new Error("Invalid type for goTo. Must be number or string.");
    }
  }

  public async goToName(name: string) {
    this.eventManager._trigger_RAGAI_EVENT(this, { name });
    const index = this._stages.findIndex(s => s.name === name);
    if (index === -1) throw new Error(`Stage with name '${name}' not found.`);
    if (this._stages[index]) {
      this._currentIndex = index;
      await this._activate();
    }
  }


  public async next() {
    this.eventManager._trigger_RAGAI_EVENT(this);
    if (this._currentIndex < this._stages.length - 1) {
      this._currentIndex++;
      await this._activate();
    } else {
      throw new Error('Already at the last stage.');
    }
  }

  public async prev() {
    this.eventManager._trigger_RAGAI_EVENT(this);
    if (this._currentIndex > 0) {
      this._currentIndex--;
      await this._activate();
    } else {
      throw new Error('Already at the first stage.');
    }
  }

  /* public get currentStage(): StageDefinition | undefined {
    return this._stages[this._currentIndex];
  } */

  public get allStages(): StageDefinition[] {
    return [...this._stages];
  }

  public get visibleStages(): StageDefinition[] {
    return this._stages.filter(stage => !stage.hidden);
  }

  public async restore(): Promise<void> {
    const savedStageName = await this.keyValService.get(this.KEYVAL_KEY_CURRENT, this.KEYVAL_TYPE);




    if (savedStageName && typeof savedStageName === "string") {
      const index = this._stages.findIndex(stage => stage.name === savedStageName);
      if (index !== -1) {
        this._currentIndex = index;
        // Damit EnterJS der neuen Stage auch ausgeführt wird
        // this.previousStage = this._stages[this._currentIndex];
        // this.currentStage = this._stages[this._currentIndex];
        await this._activate();

      } else {
        console.warn(`⚠️ Stage '${savedStageName}' not found in current stage list.`);
      }
    } else {
      console.log("ℹ️ No saved stage to restore.");
    }
  }


  public list(includeHidden = false): string[] {
    return this._stages
      .filter(stage => includeHidden || !stage.hidden)
      .map(stage => stage.name);
  }



  private async _activate() {
    const from = this.currentStage;
    const to = this._stages[this._currentIndex] ?? undefined;
    const isSameStage = from?.name + "" === to?.name + "";
    console.log("🎭 Aktiviere Stage", from, to);
    // 👉 Before-Event
    if (!isSameStage) {
      this.beforeStageChange.emit({ from, to });
    }
    // 👉 Exit-Event
    if (from && !isSameStage) {
      this.stageExit.emit(from);
    }
    this.previousStage = from;

    // WENN ALT STAGE == NEU STAGE, DANN KEINE SKILLS NICHT ANFASSEN
    if (!isSameStage) {
      await this.skillManager.disableAllSkills();
      if (to && to.skills.length > 0 && this.botIdService.getBotId()) {
        await this.skillManager.enableSkills(to.skills);
      }

      if (to?.forceSkills && this.botIdService.getBotId()) {
        await this.skillManager.skillsForNextRun(to.skills);
      } else if (this.botIdService.getBotId()) {
        await this.skillManager.skillsForNextRun([]);
      }
    }


    if (to?.footer) {
      this.DOM.showFooter();
    } else {
      this.DOM.hideFooter();
    }

    document.title = to?.title ?? "";
    await this.keyValService.storeKeyValEntry(this.KEYVAL_KEY_CURRENT, to?.name, this.KEYVAL_TYPE);
    await this.keyValService.storeKeyValEntry(this.KEYVAL_KEY_PREVIOUS, from?.name, this.KEYVAL_TYPE);

    this.currentStage = to;

    // 👉 Enter-Event
    if (!isSameStage) {
      this.stageEnter.emit(to);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.ENTER", to);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.EXIT", from);
      // Exit alte Stage, dann Enter neue Stage
      try {
        if (from?.exitFn) await from.exitFn();
      } catch (e) {
        console.error("Stage exitJS error:", e);
      }
      try {
        if (to?.enterFn) await to.enterFn();
      } catch (e) {
        console.error("Stage enterJS error:", e);
      }
    }

    // 👉 After-Event
    this.afterStageChange.emit({ from, to });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.CHANGED", { from, to });
  }

}
